// src/admin/screens/borrowers/BorrowerDetailScreen.tsx
// Borrower detail shell: hero header (avatar + name + score badge + tier
// chip + phone/email meta) with action buttons on the right, KPI strip
// (score / lifetime borrowed / outstanding / on-time rate), then a 5-tab
// body (Profile / Loans / KYC / Credit score / Notes).
//
// The "Freeze borrowing" action from the original sub-project D plan is
// intentionally NOT surfaced yet: the backend schema has no
// `is_borrowing_frozen` column, and adding one is a sub-project A concern
// per CLAUDE.md. Once that migration lands, wire a FreezeBorrowingModal
// here under the existing `borrowers.freeze` capability.
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Phone, Sliders, MessageSquare } from 'lucide-react';
import { useBorrower, pickCurrentScore } from '@admin/hooks/useBorrower';
import { useRole } from '@admin/auth/useRole';
import Button from '@admin/components/Button';
import Avatar from '@admin/components/Avatar';
import TierBadge from '@admin/components/TierBadge';
import StatusChip from '@admin/components/StatusChip';
import { formatZambiaPhone } from '@admin/utils/phone';
import ProfileTab from './tabs/ProfileTab';
import LoansTab from './tabs/LoansTab';
import KycTab from './tabs/KycTab';
import CreditScoreTab from './tabs/CreditScoreTab';
import NotesTab from './tabs/NotesTab';
import ResetPhoneModal from './modals/ResetPhoneModal';
import AdjustCreditScoreModal from './modals/AdjustCreditScoreModal';
import styles from './BorrowerDetailScreen.module.css';

const TABS = ['Profile', 'Loans', 'KYC', 'Credit score', 'Notes'] as const;
type Tab = (typeof TABS)[number];
type ActiveModal = 'reset_phone' | 'adjust_score' | null;

const KYC_TONES = {
  verified: 'positive', rejected: 'danger', manual_review: 'warn', pending: 'neutral',
} as const;
const KYC_LABELS = {
  verified: 'KYC verified', rejected: 'KYC rejected', manual_review: 'Manual review', pending: 'KYC pending',
} as const;

export default function BorrowerDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useRole();
  const { data: borrower, isLoading, error, refetch } = useBorrower(id);
  const [tab, setTab] = useState<Tab>('Profile');
  const [modal, setModal] = useState<ActiveModal>(null);

  if (isLoading) return <div className={styles.page}>Loading borrower…</div>;
  if (error) {
    return <div className={styles.page} style={{ color: 'var(--c-danger)' }}>{(error as Error).message}</div>;
  }
  if (!borrower) return <div className={styles.page}>Borrower not found.</div>;

  const score = pickCurrentScore(borrower);
  const name = borrower.display_name ?? borrower.full_name ?? 'Unnamed borrower';
  const closeModal = (refresh: boolean) => {
    setModal(null);
    if (refresh) void refetch();
  };

  const scoreToneClass = score
    ? score.current_score >= 70 ? '' : score.current_score >= 55 ? styles.warn : styles.danger
    : '';

  return (
    <div className={styles.page}>
      <button type="button" className={styles.back} onClick={() => navigate('/borrowers')}>
        <ChevronLeft size={14} /> Borrowers
      </button>

      <div className={styles.heroRow}>
        <div className={styles.heroLeft}>
          <Avatar name={name} size={64} />
          <div className={styles.heroText}>
            <div className={styles.heroChips}>
              <StatusChip
                label={KYC_LABELS[borrower.kyc_status]}
                tone={KYC_TONES[borrower.kyc_status]}
              />
              {score && <TierBadge tier={score.current_tier} />}
              {score && (
                <span className={styles.scoreBadge}>
                  {score.current_score}<span className={styles.scoreSlash}>/100</span>
                </span>
              )}
            </div>
            <h1 className={styles.heroTitle}>{name}</h1>
            <p className={styles.heroSub}>
              <span><Phone size={12} style={{ display: 'inline', verticalAlign: -1, marginRight: 4 }} />{formatZambiaPhone(borrower.phone)}</span>
              {borrower.email && <span>{borrower.email}</span>}
            </p>
          </div>
        </div>
        <div className={styles.heroActions}>
          <Button
            variant="secondary"
            leftIcon={<MessageSquare size={14} />}
            onClick={() => setModal('reset_phone')}
            disabled={!can('borrowers.reset_phone')}
          >
            Reset phone
          </Button>
          <Button
            variant="secondary"
            leftIcon={<Sliders size={14} />}
            onClick={() => setModal('adjust_score')}
            disabled={!can('actions.review')}
          >
            Adjust score
          </Button>
        </div>
      </div>

      <div className={styles.kpiGrid}>
        <div className={styles.kpiTile}>
          <div className={styles.kpiLabel}>Credit score</div>
          <div className={`${styles.kpiValue} ${scoreToneClass}`}>
            {score ? score.current_score : '—'}
          </div>
        </div>
        <div className={styles.kpiTile}>
          <div className={styles.kpiLabel}>Tier</div>
          <div className={styles.kpiValue}>
            {score ? score.current_tier : '—'}
          </div>
        </div>
        <div className={styles.kpiTile}>
          <div className={styles.kpiLabel}>KYC status</div>
          <div className={styles.kpiValue} style={{ fontSize: 18 }}>
            {KYC_LABELS[borrower.kyc_status]}
          </div>
        </div>
        <div className={styles.kpiTile}>
          <div className={styles.kpiLabel}>Phone</div>
          <div className={styles.kpiValue} style={{ fontSize: 16 }}>
            {formatZambiaPhone(borrower.phone)}
          </div>
        </div>
      </div>

      <div className={styles.tabs} role="tablist">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={tab === t ? styles.tabActive : styles.tab}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Profile' && <ProfileTab borrower={borrower} />}
      {tab === 'Loans' && <LoansTab borrower={borrower} />}
      {tab === 'KYC' && <KycTab borrower={borrower} />}
      {tab === 'Credit score' && <CreditScoreTab borrowerId={borrower.id} />}
      {tab === 'Notes' && <NotesTab borrowerId={borrower.id} />}

      {modal === 'reset_phone' && (
        <ResetPhoneModal borrower={borrower} onClose={closeModal} />
      )}
      {modal === 'adjust_score' && (
        <AdjustCreditScoreModal borrower={borrower} onClose={closeModal} />
      )}
    </div>
  );
}
